import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type NotificationSetting = {
  id: number;
  notificationType: string;
  displayName: string;
  description: string;
  isEnabled: boolean;
};

export default function MobileNotifications() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query notification settings
  const { data: settings = [], isLoading } = useQuery<NotificationSetting[]>({
    queryKey: ["/api/notifications/settings"],
    queryFn: async () => {
      const response = await fetch("/api/notifications/settings", {
        credentials: 'include'
      });
      if (!response.ok) throw new Error("Failed to fetch notification settings");
      const result = await response.json();
      return result.settings;
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ notificationType, isEnabled }: { notificationType: string; isEnabled: boolean }) => {
      const response = await fetch("/api/notifications/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ notificationType, isEnabled }),
      });
      if (!response.ok) throw new Error("Failed to update notification setting");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/settings"] });
      toast({
        title: "Success",
        description: "Notification setting updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Mobile Push Notifications</CardTitle>
          <p className="text-sm text-muted-foreground">
            Control which types of push notifications are sent to operators' mobile devices.
            When disabled, no notifications of that type will be sent to any operator.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              {settings.map((setting) => (
                <div
                  key={setting.id}
                  className="flex items-start justify-between gap-6 p-4 border rounded-lg bg-card"
                >
                  <div className="space-y-1 flex-1">
                    <div className="font-medium">{setting.displayName}</div>
                    {setting.description && (
                      <div className="text-sm text-muted-foreground">
                        {setting.description}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-2 min-w-[140px]">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {setting.isEnabled ? "Enabled" : "Disabled"}
                    </div>
                    <Switch
                      checked={setting.isEnabled}
                      onCheckedChange={(checked) => {
                        updateMutation.mutate({
                          notificationType: setting.notificationType,
                          isEnabled: checked,
                        });
                      }}
                      disabled={updateMutation.isPending}
                    />
                  </div>
                </div>
              ))}
            </div>

            {settings.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No notification types configured.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Usage Notes</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-muted-foreground">
          <p>
            • <strong>New Assignment:</strong> Automatically sent when tasks are assigned via "Send to Mobile"
          </p>
          <p>
            • <strong>Cycle Deadline Reminder:</strong> Automatically sent 24 hours before a cycle ends
          </p>
          <p>
            • <strong>Assignment Reset:</strong> Automatically sent when an admin resets a task
          </p>
          <p>
            • <strong>Store Signature Required:</strong> Automatically sent when all tasks are complete and signature is needed
          </p>
          <p className="pt-2 border-t">
            Changes take effect immediately. Operators will only receive notifications for enabled types.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

